import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal } from "./Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const projectTypes = ["Crew Supply", "Heavy Lift", "Inspection", "Lift Planning", "Other"];

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    project_type: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Name, email and message are required.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry received. Our operations desk will be in touch.");
      setForm({ name: "", email: "", company: "", project_type: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="w-full px-6 md:px-12 py-24 md:py-40 border-t border-white/10"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="font-mono text-xs tracking-[0.25em] uppercase text-accent">
              [ 005 — Request a Crew ]
            </span>
            <h2 className="font-display text-5xl md:text-7xl leading-[0.88] tracking-tight mt-6 mb-8">
              Scope your<br />next lift.
            </h2>
            <p className="text-lg text-[#94A3B8] font-light leading-relaxed max-w-md mb-12">
              Tell us the vessel, the load and the window. We&apos;ll mobilise the right
              crew and gear — anywhere the work is.
            </p>

            <div className="space-y-6 border-t border-white/10 pt-8">
              <div>
                <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#64748B] block mb-1">Operations Desk</span>
                <a href="mailto:ops@gndsolution.co" data-testid="contact-email-link" className="text-white hover:text-accent transition-colors">ops@gndsolution.co</a>
              </div>
              <div>
                <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#64748B] block mb-1">24/7 Mobilisation</span>
                <a href="tel:+4401224000000" data-testid="contact-phone-link" className="text-white hover:text-accent transition-colors">+44 (0) 1224 000 000</a>
              </div>
              <div>
                <span className="font-mono text-xs tracking-[0.2em] uppercase text-[#64748B] block mb-1">Base</span>
                <span className="text-white">Aberdeen · Rotterdam · Takoradi</span>
              </div>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15} className="lg:col-span-6 lg:col-start-7">
          <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="font-mono text-xs tracking-[0.2em] uppercase text-[#64748B] block mb-2">Name *</label>
                <input data-testid="contact-name" className="util-input" value={form.name} onChange={update("name")} placeholder="Your name" />
              </div>
              <div>
                <label className="font-mono text-xs tracking-[0.2em] uppercase text-[#64748B] block mb-2">Email *</label>
                <input data-testid="contact-email" type="email" className="util-input" value={form.email} onChange={update("email")} placeholder="you@company.com" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div>
                <label className="font-mono text-xs tracking-[0.2em] uppercase text-[#64748B] block mb-2">Company</label>
                <input data-testid="contact-company" className="util-input" value={form.company} onChange={update("company")} placeholder="Operator / contractor" />
              </div>
              <div>
                <label className="font-mono text-xs tracking-[0.2em] uppercase text-[#64748B] block mb-2">Scope</label>
                <div className="flex flex-wrap gap-2 pt-2">
                  {projectTypes.map((t) => (
                    <button
                      key={t}
                      type="button"
                      data-testid={`scope-${t.toLowerCase().replace(/\s/g, "-")}`}
                      onClick={() => setForm((f) => ({ ...f, project_type: t }))}
                      className={`font-mono text-[11px] tracking-[0.1em] uppercase px-3 py-1.5 border transition-colors duration-300 ${
                        form.project_type === t
                          ? "bg-accent text-[#050B14] border-accent"
                          : "border-white/15 text-[#94A3B8] hover:border-accent hover:text-accent"
                      }`}
                    >
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <label className="font-mono text-xs tracking-[0.2em] uppercase text-[#64748B] block mb-2">Brief *</label>
              <textarea data-testid="contact-message" rows={4} className="util-input resize-none" value={form.message} onChange={update("message")} placeholder="Vessel, load weight, location and dates..." />
            </div>

            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="group inline-flex items-center gap-3 bg-accent text-[#050B14] font-mono text-sm tracking-[0.15em] uppercase px-8 py-4 hover:bg-white transition-colors duration-300 disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send enquiry"}
              <span className="group-hover:translate-x-1 transition-transform duration-300" aria-hidden>→</span>
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
};
